import * as React from "react";
import {useRef, useState} from "react";
import {TextFieldComponent} from "../common/TextField.component";
import {RaisedButton} from "../common/RaisedButton";
import {prominentButtonStyle, secondaryButtonStyle} from "../common/buttonStyles";
import {ERROR_NOT_ALLOWED, getErrorSpecificClientMessage} from "../../utils/errorUtil";
import {formatNumericDate, reactDatePickerShortDateFormat} from "../../utils/dateUtil";
import {setHeaderNotification} from "../base/header/HeaderActions";
import {NotificationTypes} from "../base/header/HeaderReducer";
import {useDispatch} from "react-redux";
import {IReportWorkspace, PowerBiReportingApi} from "../../api/PowerBiReportingApi";
import {clearClientReports} from "../power-bi-reporting/PowerBiReporting.actions";
import DatePicker from "react-datepicker";
import {LoadingSpinner} from "../icons/LoadingSpinner.component";
import {Portal} from "react-overlays";
import {ApiError} from "../../model/ApiError.model";
import {getTranslation} from "../../utils/translationUtil";
import {AnyThunkDispatch} from "../../../commonTypes";

export interface IAddClientReportProps {
    setShowAddReport: (show: boolean) => void;
    categoryId: number;
    planId: number;
    workspace: IReportWorkspace | undefined;
}

interface IAttachment {
    key: number;
    file: File | undefined;
    title: string;
}

const FILE_INPUT_ID_PREFIX = "attachment-file-input-";
const newAttachment = (key: number): IAttachment => ({key, file: undefined, title: ""});
const getFileTitle = (fileName: string) => {
    const lastIndex = fileName.lastIndexOf(".");
    return lastIndex < 0 ? fileName : fileName.substring(0, lastIndex);
};

export const AddClientReport: React.FunctionComponent<IAddClientReportProps> = (props) => {
    const [submitClicked, setSubmitClicked] = useState(false);
    const [reportId, setReportId] = useState("");
    const [reportName, setReportName] = useState("");
    const [requestId, setRequestId] = useState("");
    const [reportDate, setReportDate] = useState<Date | undefined>(undefined);
    const nextKeyRef = useRef(1);
    const [attachments, setAttachments] = useState<IAttachment[]>([newAttachment(0)]);

    const dispatch: AnyThunkDispatch = useDispatch();

    const allocateKey = () => {
        const key = nextKeyRef.current;
        nextKeyRef.current += 1;
        return key;
    };

    const updateAttachment = (key: number, changes: Partial<IAttachment>) => {
        setAttachments((current) =>
            current.map((attachment) =>
                attachment.key === key ? {...attachment, ...changes} : attachment
            )
        );
    };

    const handleFileChosen = (key: number, files: FileList | null) => {
        if (!files || files.length === 0) {
            return;
        }
        const file = files[0];
        updateAttachment(key, {file, title: getFileTitle(file.name)});
        setSubmitClicked(false);
    };

    const removeAttachment = (key: number) => {
        setAttachments((current) => {
            const next = current.filter((a) => a.key !== key);
            return next.length === 0 ? [newAttachment(allocateKey())] : next;
        });
    };

    const addAttachment = () => {
        setAttachments((current) => [...current, newAttachment(allocateKey())]);
    };

    const renderInputField = (name: string,
                              placeHolder: string,
                              currentValue: string,
                              setFunction: (value: string) => void,
                              containerClass: string = "add-client-report__input-container") => {
        const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
            return setFunction(e.target.value);
        };

        return <div className={containerClass}>
                <div className="add-client-report__label">{placeHolder}</div>
                <TextFieldComponent
                    name={`${name}`}
                    className={`add-client-report__${name}-input`}
                    type="text"
                    fullWidth={true}
                    value={currentValue}
                    handleChange={handleChange}
                    placeholder={placeHolder}
                    renderRequired={false}
                    submitClicked={submitClicked}
                />
            </div>;
    };

    const CalendarContainer = ({children}: any) => {
        const el = document.getElementById('calendar-portal');

        return <Portal container={el}>
                {children}
            </Portal>;
    };

    const renderDateInput = () => {
        return <div className="add-client-report__date-input">
            <div className="add-client-report__label-date">Report Date</div>
            <DatePicker
                        popperContainer={CalendarContainer}
                        className="add-client-report__date-picker"
                        placeholderText="dd MMM yyyy"
                        dateFormat={reactDatePickerShortDateFormat}
                        dropdownMode="select"
                        selected={reportDate}
                        onChange={(date: Date) => {
                            setReportDate(date);
                        }}/>
        </div>;
    };

    const renderAttachment = (attachment: IAttachment, index: number) => {
        const fileInputId = `${FILE_INPUT_ID_PREFIX}${attachment.key}`;
        const onTitleChange = (value: string) => updateAttachment(attachment.key, {title: value});

        return <div
            key={attachment.key}
            className="add-client-report__attachment"
            data-testid={`add-client-report__attachment-${index + 1}`}
        >
            <h4 className="add-client-report__attachment-header">Attachment</h4>
            <div className="add-client-report__inputs">
                <div className="add-client-report__upload-file">
                    <label htmlFor={fileInputId} className="add-client-report__label-file">
                        Upload File
                    </label>
                    <input
                        id={fileInputId}
                        data-testid={fileInputId}
                        className="add-client-report__file-input"
                        type="file"
                        onChange={(e) => handleFileChosen(attachment.key, e.target.files)}
                    />
                </div>
                {renderInputField(
                    "document-title",
                    "Document Title",
                    attachment.title,
                    onTitleChange
                )}
                {/*<a role="link"*/}
                {/*   className="add-client-report__remove-file clickable"*/}
                {/*   data-testid={`add-client-report__remove-file-${index + 1}`}*/}
                {/*   onClick={() => removeAttachment(attachment.key)}>*/}
                {/*    Remove file*/}
                {/*</a>*/}
            </div>
        </div>;
    };

    const hasInvalidAttachment = () => {
        return attachments.some((attachment) => {
            const hasTitle = attachment.title.trim().length > 0;
            const hasFile = !!attachment.file;
            return (hasFile && !hasTitle) || (!hasFile && hasTitle);
        });
    };

    const isInvalidInput = () => {
        const isMissingRequiredFields = !reportDate
            || reportId.trim().length === 0
            || reportName.trim().length === 0
            || requestId.trim().length === 0
            || !props.workspace
            || props.workspace!.id.trim().length === 0;

        return isMissingRequiredFields || hasInvalidAttachment();
    };

    const renderSpinner = () => {
        return submitClicked ? <LoadingSpinner/> : null;
    };

    const clearAllAttachments = () => {
        setAttachments([newAttachment(allocateKey())]);
    };

    const submit = () => {
        setSubmitClicked(true);
        if (isInvalidInput()) {
            return;
        }

        const formData = new FormData();
        attachments
            .filter((a) => a.file && a.title.trim().length > 0)
            .forEach((a) => {
                formData.append("files", a.file as File);
                formData.append("titles", a.title);
            });

        formData.append("reportDate", formatNumericDate(reportDate!));
        formData.append("reportId", reportId);
        formData.append("reportName", reportName);
        formData.append("requestId", requestId);
        formData.append("categoryId", `${props.categoryId}`);
        formData.append("workspaceId", props.workspace!.id);

        const processSuccess = () => {
            setSubmitClicked(false);
            dispatch(setHeaderNotification(
            {message: "Added Client Report", notificationType: NotificationTypes.SUCCESS},
            5000));
            dispatch(clearClientReports());
            props.setShowAddReport(false);
        };

        const processFailure  = (errorNumber: number) => {
            setSubmitClicked(false);
            dispatch(
                setHeaderNotification({
                    message: `${getTranslation(
                        "client-reporting.failed-add-report", "Failed to Add Client Report"
                    )}${getErrorSpecificClientMessage(errorNumber)}`,
                    notificationType: NotificationTypes.FAILURE
                }, 5000)
            );
            window.scrollTo(0, 0);
            if (errorNumber === ERROR_NOT_ALLOWED) clearAllAttachments();
        };

        PowerBiReportingApi.addClientReportToPlan(props.planId, formData)
            .then((response) =>
            response.data
                ? processSuccess()
                : processFailure(response.error ? response.error.errorNumber : -1))
            .catch((error: ApiError) => processFailure(error.errorNumber));
    };

    return <div className="add-client-report__container" data-testid="add-client-report__container">
            {renderSpinner()}
            <div className="add-client-report__inputs">
                {renderInputField("report-id", "PowerBI Report ID", reportId, setReportId)}
                {renderInputField("report-name", "PowerBI Report Name", reportName, setReportName)}
                {renderDateInput()}
                {renderInputField("request-id", "Request ID", requestId, setRequestId,
                    "add-client-report__request-id-container")}
            </div>
            <h3 className="powerbi-admin__optional-fields-header">Optional File Attachment</h3>
            <div className="add-client-report__attachments">
                {attachments.map((a, idx) => renderAttachment(a, idx))}
            </div>
            {/*<RaisedButton className="add-client-report__add-attachment-button"*/}
            {/*              style={secondaryButtonStyle}*/}
            {/*              primary={false}*/}
            {/*              onClick={addAttachment}>*/}
            {/*    Add another file*/}
            {/*</RaisedButton>*/}
            <div className="add-client-report__buttons">
                <RaisedButton className="add-client-report__cancel-button"
                              style={secondaryButtonStyle}
                              primary={false}
                              onClick={() => props.setShowAddReport(false)}>
                    Cancel
                </RaisedButton>
                <div className="buttons-spacer-between"/>
                <RaisedButton className="add-client-report__save-button"
                              style={prominentButtonStyle}
                              primary={true}
                              onClick={submit}
                              disabled={isInvalidInput()}
                              >
                    Add Report
                </RaisedButton>
            </div>
        </div>;
};

export default AddClientReport;
